import { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { ACTIVAR_LICENCIA } from '../graphql/licencia'
import './Activacion.css'
import Logo from '../assets/logo-posfac.png'

// URL del API de licencias (puede configurarse en .env)
const LICENSE_API_URL = import.meta.env.VITE_LICENSE_API_URL || 'http://localhost:4003/api/v1'

function Activacion({ onActivado }) {
  // Mutation para guardar en la base de datos del hotel
  const [activarLicenciaMutation] = useMutation(ACTIVAR_LICENCIA)
  const [modo, setModo] = useState('opciones') // opciones, solicitar, activar, suspendida
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [verificandoReactivacion, setVerificandoReactivacion] = useState(false)

  // Datos para solicitud
  const [solicitudData, setSolicitudData] = useState({
    nit: '',
    nombre_comercial: '',
    razon_social: '',
    direccion: '',
    ciudad: '',
    telefono: '',
    email: '',
    tipo_interfaz: 'hotel',
    tipo_licencia: 'renta_mensual'
  })

  // Datos para activacion
  const [codigoInput, setCodigoInput] = useState('')
  const [comercioData, setComercioData] = useState(null)
  const [licenciaInfo, setLicenciaInfo] = useState(null)
  const [buscandoCodigo, setBuscandoCodigo] = useState(false)

  // Verificar si la licencia estaba suspendida al cargar
  useEffect(() => {
    const estadoLicencia = localStorage.getItem('licencia_estado')
    const codigo = localStorage.getItem('licencia_codigo')
    const nit = localStorage.getItem('comercio_nit')

    if (estadoLicencia === 'suspendida' && codigo && nit) {
      setModo('suspendida')
      verificarReactivacion()
    }
  }, [])

  // Verificar si la licencia fue reactivada
  const verificarReactivacion = async () => {
    const codigo = localStorage.getItem('licencia_codigo')
    const nit = localStorage.getItem('comercio_nit')

    if (!codigo || !nit) return

    setVerificandoReactivacion(true)

    try {
      const response = await fetch(`${LICENSE_API_URL}/estado`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo, nit })
      })

      if (response.ok) {
        const data = await response.json()

        if (data.success && data.permitirAcceso) {
          localStorage.setItem('licencia_estado', 'activa')
          if (data.fecha_vencimiento) {
            localStorage.setItem('licencia_vencimiento', data.fecha_vencimiento)
          }

          setSuccess('Licencia reactivada. Redirigiendo...')

          setTimeout(() => {
            window.location.href = '/login'
          }, 1500)
        }
      }
    } catch (err) {
      console.warn('Error verificando reactivacion:', err.message)
    } finally {
      setVerificandoReactivacion(false)
    }
  }

  const handleSolicitudChange = (e) => {
    const { name, value } = e.target
    setSolicitudData(prev => ({ ...prev, [name]: value }))
  }

  // Buscar datos del comercio por codigo
  const buscarPorCodigo = async () => {
    const codigoLimpio = codigoInput.replace(/\s/g, '').toUpperCase()

    if (codigoLimpio.length !== 32 || !/^[A-Fa-f0-9]{32}$/.test(codigoLimpio)) {
      setError('El codigo debe tener exactamente 32 caracteres hexadecimales')
      return
    }

    setBuscandoCodigo(true)
    setError(null)
    setComercioData(null)
    setLicenciaInfo(null)

    try {
      const response = await fetch(`${LICENSE_API_URL}/info-codigo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo: codigoLimpio })
      })

      const data = await response.json()

      if (data.success) {
        setComercioData(data.comercio)
        setLicenciaInfo(data.licencia)
      } else {
        setError(data.message || 'Codigo no encontrado')
      }
    } catch (err) {
      setError('Error de conexion. Verifique su conexion a internet.')
    } finally {
      setBuscandoCodigo(false)
    }
  }

  const handleSolicitarLicencia = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${LICENSE_API_URL}/solicitudes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(solicitudData)
      })

      const data = await response.json()

      if (data.success) {
        setSuccess('Solicitud enviada exitosamente. Recibira su codigo por email una vez sea aprobada.')
        localStorage.setItem('licencia_estado', 'pendiente')
      } else {
        setError(data.message || 'Error al enviar la solicitud')
      }
    } catch (err) {
      setError('Error de conexion. Verifique su conexion a internet o contacte a soporte.')
    } finally {
      setLoading(false)
    }
  }

  const handleActivarLicencia = async (e) => {
    e.preventDefault()

    if (!comercioData) {
      setError('Primero debe validar el codigo')
      return
    }

    setLoading(true)
    setError(null)

    const codigoLimpio = codigoInput.replace(/\s/g, '').toUpperCase()

    try {
      // 1. Activar en el servidor de licencias
      const response = await fetch(`${LICENSE_API_URL}/activar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo: codigoLimpio,
          nit: comercioData.nit,
          nombre_comercial: comercioData.nombre_comercial,
          razon_social: comercioData.razon_social
        })
      })

      const data = await response.json()

      if (data.success) {
        // 2. Guardar en la base de datos del hotel via GraphQL
        try {
          await activarLicenciaMutation({
            variables: {
              input: {
                codigo: codigoLimpio,
                nit: comercioData.nit,
                nombre_comercial: comercioData.nombre_comercial,
                razon_social: comercioData.razon_social,
                ciudad: comercioData.ciudad || null,
                huella: data.huella,
                fecha_vencimiento: data.fecha_vencimiento || null,
                tipo_licencia: data.tipo_licencia,
                modulos: data.modulos || [],
                modulos_version: data.modulos_version != null ? String(data.modulos_version) : null
              }
            }
          })
        } catch (dbError) {
          console.error('Error guardando en BD local:', dbError)
          setError('Licencia validada pero no se pudo guardar en la base de datos. Intente nuevamente.')
          setLoading(false)
          return
        }

        // 3. Guardar en localStorage (backup)
        localStorage.setItem('licencia_codigo', codigoLimpio)
        localStorage.setItem('licencia_huella', data.huella)
        localStorage.setItem('licencia_vencimiento', data.fecha_vencimiento || '')
        localStorage.setItem('licencia_tipo', data.tipo_licencia)
        localStorage.setItem('licencia_estado', 'activa')
        localStorage.setItem('licencia_fecha_activacion', new Date().toISOString().split('T')[0])
        localStorage.setItem('comercio_nit', comercioData.nit)
        localStorage.setItem('comercio_nombre', comercioData.nombre_comercial)
        localStorage.setItem('comercio_razon_social', comercioData.razon_social)

        if (data.modulos) {
          localStorage.setItem('licencia_modulos', JSON.stringify(data.modulos))
          localStorage.setItem('licencia_modulos_version', String(data.modulos_version || ''))
        }

        setSuccess('Licencia activada correctamente! Los datos se han guardado en el sistema.')

        setTimeout(() => {
          if (onActivado) {
            onActivado({
              codigo: codigoLimpio,
              huella: data.huella,
              fechaVencimiento: data.fecha_vencimiento,
              tipoLicencia: data.tipo_licencia
            })
          }
        }, 2000)
      } else {
        setError(data.message || 'Error al activar la licencia')
      }
    } catch (err) {
      setError('Error de conexion. Si no tiene internet, contacte a soporte para activacion manual.')
    } finally {
      setLoading(false)
    }
  }

  const renderOpciones = () => (
    <div className="activacion-opciones">
      <div className="activacion-logo">
        <img src={Logo} alt="Posfac Hotel" className="activacion-logo-image" />
        <p>Sistema de Gestion Hotelera</p>
      </div>

      <h2>Activacion del Sistema</h2>
      <p className="activacion-descripcion">
        Para comenzar a usar Posfac Hotel, necesita una licencia activa.
        Seleccione una opcion para continuar:
      </p>

      <div className="opciones-grid">
        <button
          className="opcion-card"
          onClick={() => setModo('solicitar')}
        >
          <div className="opcion-icon">📝</div>
          <h3>Solicitar Licencia</h3>
          <p>Primera vez? Solicite su licencia y reciba el codigo por email.</p>
        </button>

        <button
          className="opcion-card"
          onClick={() => setModo('activar')}
        >
          <div className="opcion-icon">🔑</div>
          <h3>Ya tengo un codigo</h3>
          <p>Ingrese su codigo de licencia para activar el sistema.</p>
        </button>
      </div>
    </div>
  )

  const renderSolicitar = () => (
    <div className="activacion-form-container">
      <button className="btn-volver" onClick={() => { setModo('opciones'); setError(null); setSuccess(null); }}>
        ← Volver
      </button>

      <h2>Solicitar Licencia</h2>
      <p className="form-descripcion">
        Complete los datos de su hotel. Recibiremos su solicitud y le enviaremos
        el codigo de activacion por email.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!success && (
        <form onSubmit={handleSolicitarLicencia} className="activacion-form">
          <div className="form-row">
            <div className="form-group">
              <label>NIT *</label>
              <input
                type="text"
                name="nit"
                value={solicitudData.nit}
                onChange={handleSolicitudChange}
                placeholder="900123456-1"
                required
              />
            </div>
            <div className="form-group">
              <label>Nombre Comercial *</label>
              <input
                type="text"
                name="nombre_comercial"
                value={solicitudData.nombre_comercial}
                onChange={handleSolicitudChange}
                placeholder="Mi Hotel"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Razon Social *</label>
            <input
              type="text"
              name="razon_social"
              value={solicitudData.razon_social}
              onChange={handleSolicitudChange}
              placeholder="Mi Hotel S.A.S."
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={solicitudData.email}
                onChange={handleSolicitudChange}
                placeholder="contacto@mihotel.com"
                required
              />
            </div>
            <div className="form-group">
              <label>Telefono</label>
              <input
                type="text"
                name="telefono"
                value={solicitudData.telefono}
                onChange={handleSolicitudChange}
                placeholder="3001234567"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Ciudad</label>
              <input
                type="text"
                name="ciudad"
                value={solicitudData.ciudad}
                onChange={handleSolicitudChange}
                placeholder="Bogota"
              />
            </div>
            <div className="form-group">
              <label>Direccion</label>
              <input
                type="text"
                name="direccion"
                value={solicitudData.direccion}
                onChange={handleSolicitudChange}
                placeholder="Calle 123 #45-67"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Tipo de Licencia *</label>
            <select
              name="tipo_licencia"
              value={solicitudData.tipo_licencia}
              onChange={handleSolicitudChange}
              required
            >
              <option value="compra">Compra (Permanente)</option>
              <option value="renta_mensual">Renta Mensual (30 dias)</option>
              <option value="renta_trimestral">Renta Trimestral (90 dias)</option>
              <option value="renta_anual">Renta Anual (365 dias)</option>
              <option value="prueba">Prueba Gratuita (7 dias)</option>
            </select>
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Enviando...' : 'Enviar Solicitud'}
          </button>
        </form>
      )}
    </div>
  )

  const renderActivar = () => {
    const resetActivacion = () => {
      setModo('opciones')
      setError(null)
      setSuccess(null)
      setCodigoInput('')
      setComercioData(null)
      setLicenciaInfo(null)
    }

    return (
      <div className="activacion-form-container">
        <button className="btn-volver" onClick={resetActivacion}>
          ← Volver
        </button>

        <h2>Activar Licencia</h2>
        <p className="form-descripcion">
          Ingrese el codigo de licencia que recibio por email.
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!success && (
          <div className="activacion-form">
            <div className="form-group">
              <label>Codigo de Licencia *</label>
              <div className="codigo-input-group">
                <input
                  type="text"
                  value={codigoInput}
                  onChange={(e) => {
                    setCodigoInput(e.target.value)
                    if (comercioData) {
                      setComercioData(null)
                      setLicenciaInfo(null)
                    }
                  }}
                  placeholder="Pegue aqui su codigo de 32 caracteres"
                  maxLength={40}
                  className="codigo-input"
                  disabled={buscandoCodigo}
                />
                <button
                  type="button"
                  className="btn-validar"
                  onClick={buscarPorCodigo}
                  disabled={buscandoCodigo || codigoInput.length < 32}
                >
                  {buscandoCodigo ? 'Validando...' : 'Validar'}
                </button>
              </div>
              <small>32 caracteres hexadecimales (sin espacios)</small>
            </div>

            {comercioData && (
              <div className="comercio-info-card">
                <h3>Datos del Comercio</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">NIT</span>
                    <span className="info-value">{comercioData.nit}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Nombre Comercial</span>
                    <span className="info-value">{comercioData.nombre_comercial}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Razon Social</span>
                    <span className="info-value">{comercioData.razon_social}</span>
                  </div>
                  {comercioData.ciudad && (
                    <div className="info-item">
                      <span className="info-label">Ciudad</span>
                      <span className="info-value">{comercioData.ciudad}</span>
                    </div>
                  )}
                </div>

                {licenciaInfo && (
                  <div className="licencia-info">
                    <div className="info-item">
                      <span className="info-label">Tipo de Licencia</span>
                      <span className="info-value badge-tipo">{licenciaInfo.tipo_licencia_nombre}</span>
                    </div>
                    {licenciaInfo.fecha_vencimiento && licenciaInfo.tipo_licencia !== 'compra' && (
                      <div className="info-item">
                        <span className="info-label">Vencimiento</span>
                        <span className="info-value">{licenciaInfo.fecha_vencimiento}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-note success">
                  <strong>Codigo valido.</strong> Verifique que los datos sean correctos y presione Activar.
                </div>

                <button
                  type="button"
                  className="btn-submit"
                  onClick={handleActivarLicencia}
                  disabled={loading}
                >
                  {loading ? 'Activando...' : 'Activar Licencia'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderSuspendida = () => (
    <div className="activacion-form-container">
      <div className="activacion-logo" style={{ marginBottom: '1.5rem' }}>
        <img src={Logo} alt="Posfac Hotel" className="activacion-logo-image" />
      </div>

      <div className="alert alert-error" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 0.5rem 0' }}>Licencia Suspendida</h3>
        <p style={{ margin: 0 }}>
          Su licencia ha sido suspendida. Contacte al administrador para mas informacion.
        </p>
      </div>

      {success && <div className="alert alert-success">{success}</div>}

      <div style={{ textAlign: 'center' }}>
        <button
          className="btn-submit"
          onClick={verificarReactivacion}
          disabled={verificandoReactivacion}
          style={{ marginBottom: '1rem' }}
        >
          {verificandoReactivacion ? 'Verificando...' : 'Verificar Reactivacion'}
        </button>

        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Si su licencia ha sido reactivada, presione el boton para continuar.
        </p>
      </div>
    </div>
  )

  return (
    <div className="activacion-page">
      <div className="activacion-container">
        {modo === 'opciones' && renderOpciones()}
        {modo === 'solicitar' && renderSolicitar()}
        {modo === 'activar' && renderActivar()}
        {modo === 'suspendida' && renderSuspendida()}
      </div>
    </div>
  )
}

export default Activacion
